import {
  Box,
  Button,
  Card,
  Group,
  rem,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Timeline,
  alpha,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { graphql } from "gql.tada";
import { useMutation, useQuery } from "@apollo/client/react";
import { useState } from "react";
import {
  IconArrowRight,
  IconBuildingStore,
  IconCheck,
  IconCreditCard,
  IconExternalLink,
  IconPercentage,
  IconReceipt,
  IconSettings,
  IconShieldCheck,
} from "@tabler/icons-react";
import { PageHeader } from "../../../components/PageHeader";
import { DetailPageSkeleton } from "../../../components/SkeletonLoader";
import { StatusBadge } from "../../../components/StatusBadge";

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const ORGANIZATION_PAYMENT_COLLECTION_CONFIGURATION = graphql(`
  query organizationPaymentCollectionConfiguration {
    organizationPaymentCollectionConfiguration {
      wellPlayedFeePercent
      wellPlayedMinimalFee
    }
  }
`);

const ORGANIZATION_PAYMENT_COLLECTION_CREATE_OR_UPDATE_ACCOUNT = graphql(`
  mutation organizationPaymentCollectionConfigurationCreateOrUpdateAccount {
    organizationPaymentCollectionConfigurationCreateOrUpdateAccount
  }
`);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = createStyles((theme) => ({
  page: {
    maxWidth: rem(1200),
  },

  // Status card
  statusCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  statusHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing.md,
    padding: theme.spacing.lg,
    borderBottom: `1px solid ${theme.colors.dark[6]}`,
  },
  statusIcon: {
    width: rem(48),
    height: rem(48),
    borderRadius: theme.radius.xl,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statusConfigured: {
    backgroundColor: alpha(theme.colors.green[7], 0.12),
    color: theme.colors.green[4],
  },
  statusNotConfigured: {
    backgroundColor: alpha(theme.colors.amber[7], 0.12),
    color: theme.colors.amber[4],
  },

  // Fee cards
  feeCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    textAlign: "center" as const,
  },
  feeValue: {
    fontSize: rem(32),
    fontWeight: 800,
    lineHeight: 1,
    color: theme.colors.dark[0],
  },
  feeLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 500,
    color: theme.colors.dark[3],
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
  feeDescription: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[4],
    lineHeight: 1.5,
  },

  // Info section
  infoCard: {
    backgroundColor: alpha(theme.colors.cyan[9], 0.04),
    border: `1px solid ${alpha(theme.colors.cyan[5], 0.12)}`,
  },
  infoTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    color: theme.colors.dark[1],
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },

  // Feature list
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: rem(12),
    padding: `${rem(8)} 0`,
  },
  featureIcon: {
    flexShrink: 0,
    marginTop: rem(2),
    color: theme.colors.cyan[5],
  },
  featureTitle: {
    fontWeight: 600,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[0],
  },
  featureDesc: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
    lineHeight: 1.5,
  },

  // CTA not configured
  ctaCard: {
    backgroundColor: alpha(theme.colors.violet[9], 0.06),
    border: `1px solid ${alpha(theme.colors.violet[5], 0.2)}`,
  },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SellerSettings = () => {
  const { classes } = useStyles();
  const { loading, data } = useQuery<any>(
    ORGANIZATION_PAYMENT_COLLECTION_CONFIGURATION,
  );
  const [configureAccount] = useMutation<any>(
    ORGANIZATION_PAYMENT_COLLECTION_CREATE_OR_UPDATE_ACCOUNT,
  );
  const [configureLoading, setConfigureLoading] = useState(false);

  const config = data?.organizationPaymentCollectionConfiguration;
  const isConfigured = !!config;

  const handleConfigure = async () => {
    setConfigureLoading(true);
    try {
      const result = await configureAccount();
      const url =
        result.data
          ?.organizationPaymentCollectionConfigurationCreateOrUpdateAccount;
      if (!url) return;

      if (isConfigured) {
        window.open(url);
      } else {
        window.location.href = url;
      }
    } finally {
      setConfigureLoading(false);
    }
  };

  if (loading) {
    return <DetailPageSkeleton />;
  }

  return (
    <Stack gap="xl" className={`${classes.page} wp-fade-in`}>
      {/* Page Header */}
      <PageHeader
        title="Seller Settings"
        subtitle="Configure your organization to accept payments through Stripe Connect"
        showBreadcrumbs={false}
        actions={
          isConfigured
            ? [
                {
                  label: "Update Settings",
                  icon: <IconExternalLink size={16} />,
                  onClick: handleConfigure,
                  loading: configureLoading,
                  variant: "light",
                },
              ]
            : []
        }
      />

      {/* Status Card */}
      <Card className={classes.statusCard} radius="lg" p={0}>
        <div className={classes.statusHeader}>
          <Group gap="md">
            <div
              className={`${classes.statusIcon} ${
                isConfigured
                  ? classes.statusConfigured
                  : classes.statusNotConfigured
              }`}
            >
              {isConfigured ? (
                <IconCheck size={24} />
              ) : (
                <IconSettings size={24} />
              )}
            </div>
            <Stack gap={2}>
              <Text fw={600} size="lg" c="dark.0">
                Payment Collection
              </Text>
              <Text size="sm" c="dimmed">
                {isConfigured
                  ? "Your organization is set up to receive payments from buyers."
                  : "Set up Stripe Connect to start accepting payments on the platform."}
              </Text>
            </Stack>
          </Group>
          <StatusBadge
            status={isConfigured ? "active" : "pending"}
            label={isConfigured ? "Configured" : "Not Configured"}
          />
        </div>

        <Box p="lg">
          {isConfigured ? (
            <Group gap="md">
              <ThemeIcon size="md" radius="xl" variant="light" color="green">
                <IconShieldCheck size={14} />
              </ThemeIcon>
              <Text size="sm" c="dimmed">
                Your Stripe Connect account is linked. Payments from buyers will
                be deposited directly into your connected account.
              </Text>
            </Group>
          ) : (
            <Stack gap="md">
              <Text size="sm" c="dimmed" lh={1.6}>
                To sell products, event tickets, or accept tournament entry fees
                on the platform, you need to connect a Stripe account. This is a
                one-time setup that takes only a few minutes.
              </Text>
              <Button
                size="md"
                className="wp-btn-press"
                leftSection={<IconCreditCard size={18} />}
                rightSection={<IconArrowRight size={16} />}
                loading={configureLoading}
                onClick={handleConfigure}
              >
                Connect with Stripe
              </Button>
            </Stack>
          )}
        </Box>
      </Card>

      {/* Fee Details (when configured) */}
      {isConfigured && (
        <Box>
          <Text className={classes.infoTitle} mb="md">
            Your Current Fees
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} className="wp-stagger-children">
            <Card className={classes.feeCard} p="xl" radius="lg">
              <Stack gap="sm" align="center">
                <ThemeIcon size="lg" radius="xl" variant="light" color="cyan">
                  <IconPercentage size={20} />
                </ThemeIcon>
                <Text className={classes.feeValue}>
                  {(config.wellPlayedFeePercent * 100).toFixed(1)}%
                </Text>
                <Text className={classes.feeLabel}>Platform Fee</Text>
                <Text className={classes.feeDescription}>
                  Applied as a percentage on each transaction processed through
                  the platform.
                </Text>
              </Stack>
            </Card>

            <Card className={classes.feeCard} p="xl" radius="lg">
              <Stack gap="sm" align="center">
                <ThemeIcon size="lg" radius="xl" variant="light" color="violet">
                  <IconReceipt size={20} />
                </ThemeIcon>
                <Text className={classes.feeValue}>
                  {(config.wellPlayedMinimalFee / 100).toFixed(2)}
                  <Text span size="lg" c="dark.3">
                    {" "}
                    $
                  </Text>
                </Text>
                <Text className={classes.feeLabel}>Minimum Fee</Text>
                <Text className={classes.feeDescription}>
                  The minimum fee charged per transaction, regardless of
                  transaction amount.
                </Text>
              </Stack>
            </Card>
          </SimpleGrid>
        </Box>
      )}

      {/* How It Works */}
      <Box>
        <Text className={classes.infoTitle} mb="md">
          How It Works
        </Text>
        <Card className={classes.infoCard} p="lg" radius="lg">
          <Timeline
            active={isConfigured ? 3 : 0}
            bulletSize={28}
            lineWidth={2}
            color="cyan"
          >
            <Timeline.Item
              bullet={<IconCreditCard size={14} />}
              title={
                <Text fw={600} size="sm" c="dark.0">
                  Connect your Stripe account
                </Text>
              }
            >
              <Text size="xs" c="dimmed" lh={1.6} mt={4}>
                Link your existing Stripe account or create a new one. Stripe
                handles identity verification and compliance.
              </Text>
            </Timeline.Item>

            <Timeline.Item
              bullet={<IconBuildingStore size={14} />}
              title={
                <Text fw={600} size="sm" c="dark.0">
                  Set up your products
                </Text>
              }
            >
              <Text size="xs" c="dimmed" lh={1.6} mt={4}>
                Create event tickets, tournament entry fees, or shop items in
                the platform. Pricing is fully customizable.
              </Text>
            </Timeline.Item>

            <Timeline.Item
              bullet={<IconReceipt size={14} />}
              title={
                <Text fw={600} size="sm" c="dark.0">
                  Buyers make purchases
                </Text>
              }
            >
              <Text size="xs" c="dimmed" lh={1.6} mt={4}>
                Payments are securely processed through Stripe. Buyers can pay
                with credit cards, Apple Pay, Google Pay, and more.
              </Text>
            </Timeline.Item>

            <Timeline.Item
              bullet={<IconCheck size={14} />}
              title={
                <Text fw={600} size="sm" c="dark.0">
                  Receive your payouts
                </Text>
              }
            >
              <Text size="xs" c="dimmed" lh={1.6} mt={4}>
                Funds are automatically transferred to your bank account on a
                rolling basis, minus the platform fee.
              </Text>
            </Timeline.Item>
          </Timeline>
        </Card>
      </Box>

      {/* Features Grid */}
      <Box>
        <Text className={classes.infoTitle} mb="md">
          What You Can Sell
        </Text>
        <SimpleGrid
          cols={{ base: 1, sm: 3, md: 3 }}
          className="wp-stagger-children"
        >
          {[
            {
              icon: IconReceipt,
              title: "Event Tickets",
              description:
                "Sell tickets with QR code validation, multiple tiers, early bird pricing, and capacity management.",
              color: "cyan" as const,
            },
            {
              icon: IconBuildingStore,
              title: "Shop Products",
              description:
                "Set up a digital storefront with items, bundles, and in-game currencies for your community.",
              color: "violet" as const,
            },
            {
              icon: IconReceipt,
              title: "Tournament Fees",
              description:
                "Charge entry fees for competitive tournaments with automatic team registration on payment.",
              color: "green" as const,
            },
          ].map((feature) => (
            <Card
              key={feature.title}
              p="lg"
              radius="lg"
              style={(theme) => ({
                backgroundColor: theme.colors.dark[7],
                border: `1px solid ${theme.colors.dark[5]}`,
              })}
              className="wp-card-interactive"
            >
              <Stack gap="sm">
                <ThemeIcon
                  size="lg"
                  radius="xl"
                  variant="light"
                  color={feature.color}
                >
                  <feature.icon size={20} />
                </ThemeIcon>
                <Text className={classes.featureTitle}>{feature.title}</Text>
                <Text className={classes.featureDesc}>
                  {feature.description}
                </Text>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      </Box>
    </Stack>
  );
};
